import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  loginStart,
  loginSuccess,
  loginFailure,
  authRdv,
} from "../../store/slices/userSlice";
import { useNavigate } from "react-router-dom";

import Checkbox from "../../components/inputs/checkbox";
import { toast } from "react-toastify";
import CryptoJS from "crypto-js";
import * as Yup from "yup";
import { fetchFavoris } from "../../store/slices/favorisSlice";
import { fetchRendezvous } from "../../store/slices/rendezVousSlice";
import { GoogleOAuthProvider } from "@react-oauth/google";
import GoogleLoginButton from "../../components/googleBtn";
import Divider from "../../components/divider";
import api from "../../services/interceptor";
import { fetchReviews } from "../../store/slices/avisSlice";
const LoginForm = ({ from = "login", id, after, appointmentData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.user);
  const formikRef = useRef();

  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  // Validation Schema
  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Email invalide").required("L'email est requis"),
    password: Yup.string().required("Le mot de passe est requis"),
  });

  // Handle Login Function
  const handleLogin = async (values, { setSubmitting }) => {
    dispatch(loginStart());

    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}login`, {
        email: values.email,
        password: values.password,
      });

      const { token, user, adresse_facturation } = response?.data;
      const idEtablissement = response?.data?.user?.etablissement?.id;
      dispatch(loginSuccess({ adresse_facturation, user, token }));

      // Remember me functionality
      if (values.rememberMe) {
        const encryptedPassword = CryptoJS.AES.encrypt(
          values.password,
          process.env.REACT_APP_SECRET_KEY
        ).toString();
        localStorage.setItem("email", values.email);
        localStorage.setItem("password", encryptedPassword);
      } else {
        localStorage.removeItem("email");
        localStorage.removeItem("password");
      }
      // Fetch favorites after successful login
      await dispatch(fetchFavoris()).unwrap();
      await dispatch(fetchRendezvous()).unwrap();
      // console.log("user.role",user.role)
      if (user.role == "prestataire") {
        if (idEtablissement) {
          await dispatch(fetchReviews({ idEtablissement, page: 1 })).unwrap();
        } else {
          navigate("/personalInformations");
          return;
        }
      }

      if (from === "rdv") {
        // console.log('hereee',user)
        if (user?.role === "prestataire") {
          toast.error(
            "Avec un compte prestataire vous pouvez pas réserver un rendez-vous, merci de créer un compte client ."
          );
          return;
        }
        dispatch(authRdv(true));
        after();
        // navigate(`/facturation/${id}`)
      } else {
        navigate(user?.role==="prestataire"?'/compte/prestataire/accueil':"/compte/rendez-vous");
        return;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Échec de la connexion";
      toast.error(errorMessage);
      dispatch(loginFailure(errorMessage));
    } finally {
      setSubmitting(false);
    }
  };
  // Load saved credentials
  useEffect(() => {
    const savedEmail = localStorage.getItem("email");
    const encryptedPassword = localStorage.getItem("password");

    if (savedEmail && encryptedPassword && formikRef.current) {
      const decryptedPassword = CryptoJS.AES.decrypt(
        encryptedPassword,
        process.env.REACT_APP_SECRET_KEY
      ).toString(CryptoJS.enc.Utf8);

      formikRef.current.setValues({
        email: savedEmail,
        password: decryptedPassword,
        rememberMe: true,
      });
    }
  }, []);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        email: "",
        password: "",
        rememberMe: false,
      }}
      validationSchema={validationSchema}
      onSubmit={handleLogin}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <div className={`w-100 ${from === "rdv" ? "p-24 bg-white" : ""}`}>
          <h3 className="grey500 text-center">Connexion</h3>

          <GoogleOAuthProvider
            clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
          >
            <Form className="login-form w-100 mt-24">
              {/* Email Field */}
              <div className="input-wrapper">
                <label className="form-label">Email*</label>
                <Field
                  name="email"
                  type="email"
                  className={` ${
                    touched.email && errors.email ? "is-invalid" : ""
                  }`}
                  placeholder="john@gmail.com"
                />
                <ErrorMessage name="email">
                  {(msg) => (
                    <div className="error-message mt-1">
                      <img
                        src="/images/alert-circle.svg"
                        className="arrow mr-8"
                        alt="Error"
                      />
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
              </div>

              {/* Password Field */}
              <div className="input-wrapper password mt-16">
                <label className="form-label">Mot de passe*</label>
                <div className="password-input-wrapper">
                  <Field
                    name="password"
                    type={isVisible ? "text" : "password"} // Toggle between text and password
                    // as={PasswordInput}
                    className={`${
                      touched.password && errors.password ? "is-invalid" : ""
                    }`}
                    placeholder="Mot de passe"
                  />
                  <span
                    className="visibility-toggle"
                    onClick={toggleVisibility}
                  >
                    <img
                      src={
                        isVisible
                          ? "/images/eye-closed.svg"
                          : "/images/eye-open.png"
                      }
                      alt="eye toggle"
                      width={24}
                      height={24}
                    />
                  </span>
                </div>
                <ErrorMessage name="password">
                  {(msg) => (
                    <div className="error-message mt-8">
                      <img
                        src="/images/alert-circle.svg"
                        className="arrow mr-8"
                        alt="Arrow"
                      />
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
                {(!touched.password || !errors.password) && (
                  <span className="mt-8 legend grey66">
                    Le mot de passe doit faire plus de 8 caractères
                  </span>
                )}
              </div>

              <div className="checkbox-container row mt-16 gap-2">
                <div className="remember-me-container col-xs-12 col-lg-6">
                  <Checkbox
                    id="remember-me"
                    label="Se souvenir de moi"
                    checked={values.rememberMe}
                    onChange={(e) =>
                      setFieldValue("rememberMe", e.target.checked)
                    }
                  />
                </div>
                <div className="forgot-password-container col-xs-12 col text-lg-end">
                  <a href="/mot-de-passe-oublie" className="link-blue">
                    Mot de passe oublié ?
                  </a>
                </div>
              </div>

              <button
                type="submit"
                className="twyny-cta-primary w-100 mt-24"
                disabled={isSubmitting || loading}
              >
                {isSubmitting || loading ? "Connexion..." : "Se connecter"}
              </button>

              {from === "login" && (
                <div className="signup-container mt-32">
                  <p className="body grey66 mr-8">
                    Pas encore de compte ?
                    <a href="/register" className="link-blue ml-8">
                      Inscrivez-vous ici
                    </a>
                  </p>
                </div>
              )}
              <Divider />
            </Form>

            {from === "rdv" && (
              <>
                <button
                  onClick={() =>
                    navigate(`/register-rdv/${id}`, { state: appointmentData })
                  }
                  className="twyny-cta-secondary w-100 "
                >
                  Créer un compte
                </button>

                <div className="mt-24"></div>
              </>
            )}
            <GoogleLoginButton
              from={from}
              id={id}
              after={after}
              appointmentData={appointmentData}
            />
          </GoogleOAuthProvider>
        </div>
      )}
    </Formik>
  );
};
export default LoginForm;
